import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Button, Card, Col, Container, Modal, Row, Form } from "react-bootstrap"
import { getUsers, addUser, editUser, enableUser, disableUser, importEntesFromEbroker } from "../../actions/auth/usersActions"
import { getSucursals } from "../../actions/other/sucursalsActions"
import { RootState, AppDispatch } from "../../store"
import { CustomDataTable } from "../../components/design/Datatables/CustomDataTable"
import UserForm from "../../components/forms/UserForm"
import Swal from "sweetalert2"
import { FormMode } from "../../types/common"
import Loader from "../../components/loaders/Loader"
import { Pencil } from "lucide-react"
import { useAuth } from "../../components/auth/useAuth"

export interface User {
    _id?: string
    user?: string
    rol?: string
    isAdminClerk?: boolean
    password?: string
    email?: string
    idSucursal?: number
    sucursal?: string
    idEnte?: string
    active?: boolean
}

export default function Users() {
    const { t } = useTranslation()
    const { clientauth } = useAuth()
    const dispatch: AppDispatch = useDispatch()

    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState<number>(25)
    const [search, setSearch] = useState<string>("")
    const [searchAux, setSearchAux] = useState('')
    const [sort, setSort] = useState<any>(null)
    const [totalRows, setTotalRows] = useState<number>(0)

    const { user, users, resultuser, total } = useSelector((state: RootState) => state.users)
    const { sucursals } = useSelector((state: RootState) => state.sucursals)

    const [modalShow, setModalShow] = useState<boolean>(false)
    const [formState, setFormState] = useState<User>({})
    const [isAddingOrEditing, setIsAddingOrEditing] = useState<FormMode>(null)
    const [isRegistering, setIsRegistering] = useState<boolean>(false)

    useEffect(() => {
        setTotalRows(total || 0)
    }, [total])

    const handleSearch = () => {
        setSearch(searchAux)
        setPage(1)
        dispatch(getUsers(1, 999999, searchAux) as any)
    }

    useEffect(() => {
        if (resultuser) {
            setModalShow(false)
            setIsRegistering(false)
        }
    }, [resultuser])

    useEffect(() => {
        if (clientauth) {
            dispatch(getUsers(1, 999999, search) as any)
            dispatch(getSucursals() as any)
        }
    }, [dispatch, t, clientauth])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const target = e.target as HTMLInputElement | HTMLSelectElement
        const { name, value, type } = target
        const inputValue = type === "checkbox" ? (target as HTMLInputElement).checked : value

        setFormState({
            ...formState,
            [name]: inputValue,
        })
    }

    const openModal = () => {
        setIsAddingOrEditing("adding")
        setFormState({})
        setModalShow(true)
    }

    const selectUser = (user: User) => {
        setFormState({ ...user, password: "" })
        setIsAddingOrEditing("editing")
        setModalShow(true)
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setIsRegistering(true)

        if (isAddingOrEditing === "editing") {
            if (formState._id) {
                dispatch(editUser(formState, formState._id) as any)
            }
        } else {
            dispatch(addUser(formState) as any)
        }

        setIsRegistering(false)
    }
    const handleImportEntes = () => {
        Swal.fire({
            title: t('userTranslate.importEntes')!,
            text: t('userTranslate.importEntesAreYouSure')!,
            showDenyButton: true,
            confirmButtonText: t('global.ok')!,
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(importEntesFromEbroker())
            }
        })
    }

    useEffect(() => {
        if (resultuser) {
            let resultusernow = resultuser
            dispatch({ type: 'USER_RESET_RESULT' })
            switch (resultusernow) {
                case 'success':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('userTranslate.addsuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break;
                case 'successEnable':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('userTranslate.successEnable')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break;
                case 'successDisable':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('userTranslate.successDisable')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break;
                case 'email':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.ok'),
                        icon: 'warning',
                        text: t('userTranslate.emailduplicated')
                    })
                    break;
                case 'needMoreData':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.ok'),
                        icon: 'warning',
                        text: t('userTranslate.needMoreData')
                    })
                    break;
                case 'editSuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('userTranslate.editSuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break;
                default:
                    break;
            }
        }
        // eslint-disable-next-line
    }, [resultuser])

    const columnsUser = [
        {
            name: t("userTranslate.idEnte"),
            grow: 1,
            cell: (user: User) => (
                <div className="cursor" onClick={() => selectUser(user)}>
                    {user.idEnte}
                </div>
            ),
            selector: (user: User) => user.idEnte || "",
            sortable: true,
        },
        {
            name: t("userTranslate.user"),
            id: "user",
            grow: 3,
            cell: (user: User) => (
                <div className="cursor" onClick={() => selectUser(user)}>
                    {user.user}
                </div>
            ),
            selector: (user: User) => user.user || "",
            sortable: true,
        }, {
            name: t("userTranslate.rol"),
            id: "rol",
            grow: 1,
            cell: (user: User) => (
                <div className="cursor" onClick={() => selectUser(user)}>
                    {t(user.rol || "")} {user.isAdminClerk && user.rol === "clerk" ? "(Admin)" : ""}
                </div>
            ),
            selector: (user: User) => user.rol || "",
            sortable: true,
        }, {
            name: t("userTranslate.email"),
            id: "email",
            grow: 2,
            cell: (user: User) => (
                <div className="cursor" onClick={() => selectUser(user)}>
                    {user.email}
                </div>
            ),
            selector: (user: User) => user.email || "",
            sortable: true,
        }, {
            name: t("userTranslate.sucursal"),
            id: "sucursal",
            grow: 2,
            cell: (user: User) => (
                <div className="cursor" onClick={() => selectUser(user)}>
                    {user.sucursal}
                </div>
            ),
            selector: (user: User) => user.sucursal || "",
            sortable: true,
        }, {
            name: t("userTranslate.active"),
            id: "active",
            grow:1,
            cell: (user: User) => (
                <div className="cursor" onClick={() => dispatch(user.active ? disableUser(user._id!) : enableUser(user._id!))}>
                    {user.active ? (
                        <img src="../images/check.svg" alt="check" width={25}></img>
                    ) : (
                        <img src="../images/times.svg" alt="times" width={25}></img>
                    )}
                </div>
            ),
            selector: (user: User) => String(user.active),
            sortable: true,
        }, {
            name: "",
            grow: 1,
            cell: (user: User) => (
                <div className="cursor" onClick={() => selectUser(user)}>
                    <Pencil size={16} />
                </div>
            ),
        }
    ]

    return (
        <Container className='p-1 py-5 p-md-5'>
            <Row>
                <Col xs={12}>
                    <h1>{t('menus.users')}</h1>
                </Col>
                <Col xs={12} className='d-flex justify-content-end'>
                    <button className="btn btn-primary" onClick={handleImportEntes}>
                        {t("userTranslate.importEntes")}
                    </button>
                    <button className="btn btn-primary ms-2" onClick={openModal}>
                        {t("userTranslate.addUser")}
                    </button>
                </Col>
            </Row>
            <Row>
                <Col xs={12} className='my-3'>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body>
                                    <Row className="justify-content-end">
                                        <Col xs={12} lg={4} className="d-flex">
                                            <input
                                                type="text"
                                                className="form-control me-2 input-search"
                                                placeholder={t("datatables.buscar")}
                                                value={searchAux || ""}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setSearchAux(e.target.value);
                                                    setPage(1);
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        handleSearch();
                                                    }
                                                }}
                                            />
                                            <Button
                                                className="button-search"
                                                onClick={handleSearch}
                                            >
                                                <i className="fas fa-search" />
                                            </Button>
                                        </Col>
                                    </Row>

                                    {users?.length > 0 ? (
                                        <CustomDataTable
                                            columns={columnsUser}
                                            data={users}
                                            pagination
                                        />
                                    ) : (
                                        <h4 className="mt-4">{t("userTranslate.thereisnotuser")}</h4>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Modal
                show={modalShow}
                onHide={() => setModalShow(false)}
                aria-labelledby="contained-modal-title-vcenter"
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        <h2>{isAddingOrEditing === 'editing' ? t('userTranslate.userEdit') : t('userTranslate.addUser')}</h2>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <UserForm
                        state={formState}
                        usertype="admin"
                        sucursals={sucursals}
                        handleInputChange={handleInputChange}
                        submit={handleSubmit}
                        isAddingOrEditing={isAddingOrEditing}
                        isRegistering={isRegistering}
                    />
                </Modal.Body>
            </Modal>
        </Container>
    )
}
